import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  Divider,
  LinearProgress,
  InputAdornment,
  TablePagination,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Water as WaterIcon,
  LocalShipping as LocalShippingIcon,
  Medication as MedicationIcon,
  Sell as SellIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Search,
  Refresh,
  ExpandMore,
  ExpandLess,
  Assignment,
  PendingActions,
  ThumbUp,
  ThumbDown,
  PriorityHigh,
  Person,
  Pool,
  AttachMoney,
  ArrowUpward,
  Description,
} from '@mui/icons-material';
import { requestsAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const REQUEST_TYPES = {
  seed: { label: 'Seed', icon: <WaterIcon sx={{ fontSize: 16 }} />, color: '#16a34a', bg: '#dcfce7' },
  feed: { label: 'Feed', icon: <LocalShippingIcon sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fef3c7' },
  medicine: { label: 'Medicine', icon: <MedicationIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fecaca' },
  crop_sale: { label: 'Crop Sale', icon: <SellIcon sx={{ fontSize: 16 }} />, color: '#2563eb', bg: '#dbeafe' },
  loan: { label: 'Loan', icon: <AccountBalanceIcon sx={{ fontSize: 16 }} />, color: '#7c3aed', bg: '#ede9fe' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#d97706', bg: '#fef3c7', border: '#fbbf24' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7', border: '#22c55e' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fecaca', border: '#f87171' },
  fulfilled: { label: 'Fulfilled', color: '#0891b2', bg: '#cffafe', border: '#22d3ee' },
};

const PRIORITY_CONFIG = {
  high: { label: 'High', color: '#dc2626', bg: '#fef2f2' },
  medium: { label: 'Medium', color: '#d97706', bg: '#fffbeb' },
  low: { label: 'Low', color: '#16a34a', bg: '#f0fdf4' },
};

const LOAN_TYPES = {
  crop_investment: 'Crop Investment',
  input_purchase: 'Input Purchase',
  equipment: 'Equipment',
  operational: 'Operational',
};

const HEADER_SX = {
  fontWeight: 700,
  fontSize: '0.68rem',
  backgroundColor: '#0f172a',
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: 'none',
  py: 1.2,
  px: 1.5,
  whiteSpace: 'nowrap',
};

// ═══════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/** Derive priority from request data */
const derivePriority = (req) => {
  if (req.request_type === 'loan' && req.loan_amount && req.loan_amount > 50000) return 'high';
  if (req.request_type === 'medicine') return 'high';
  if (req.estimated_cost && req.estimated_cost > 25000) return 'high';
  if (req.request_type === 'crop_sale') return 'medium';
  if (req.estimated_cost && req.estimated_cost > 10000) return 'medium';
  return 'low';
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (val) => {
  if (!val) return '—';
  return `₹${Number(val).toLocaleString('en-IN')}`;
};

// ═══════════════════════════════════════════════════════════════
// KPI CARD
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, onClick, active }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid #e2e8f0',
      transition: 'all 0.2s ease',
      background: active ? `${color}08` : '#fff',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20`, borderColor: color } : {},
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// DETAIL PANEL (right side)
// ═══════════════════════════════════════════════════════════════

const DetailRow = ({ label, value, mono }) => (
  <Box className="flex items-start justify-between py-1">
    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', minWidth: 100, flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', fontFamily: mono ? 'monospace' : 'inherit' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const DetailSection = ({ title, icon, children, color = '#1e293b' }) => (
  <Box sx={{ mb: 2 }}>
    <Box className="flex items-center gap-1.5 mb-1">
      {icon && React.cloneElement(icon, { sx: { fontSize: 14, color } })}
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {title}
      </Typography>
    </Box>
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {children}
      </CardContent>
    </Card>
  </Box>
);

const RequestDetailPanel = ({ request, onApprove, onReject, onClose }) => {
  if (!request) {
    return (
      <Box className="flex flex-col items-center justify-center h-full" sx={{ color: '#cbd5e1' }}>
        <Assignment sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#94a3b8' }}>Select a request</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', mt: 0.5 }}>Click on any row to view details</Typography>
      </Box>
    );
  }

  const typeInfo = REQUEST_TYPES[request.request_type] || { label: request.request_type, icon: <InfoIcon />, color: '#64748b', bg: '#f1f5f9' };
  const statusConf = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const priority = derivePriority(request);
  const prioConf = PRIORITY_CONFIG[priority];
  const isPending = request.status === 'pending';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header ── */}
      <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '8px 8px 0 0', flexShrink: 0 }}>
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-2">
            <Avatar sx={{ width: 32, height: 32, bgcolor: typeInfo.bg, color: typeInfo.color }}>
              {typeInfo.icon}
            </Avatar>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>
                {request.request_number}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                {typeInfo.label} Request
              </Typography>
            </Box>
          </Box>
          <Box className="flex items-center gap-1">
            <Chip label={statusConf.label} size="small"
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: statusConf.bg, color: statusConf.color, border: `1px solid ${statusConf.border}` }} />
            <Chip label={prioConf.label} size="small"
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: prioConf.bg, color: prioConf.color }} />
            <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8', ml: 0.5 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* ── Scrollable Content ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* Request Metadata */}
        <DetailSection title="Request Info" icon={<Description />} color="#2563eb">
          <DetailRow label="Title" value={request.title} />
          <Divider sx={{ my: 0.5 }} />
          <DetailRow label="Type" value={typeInfo.label} />
          <Divider sx={{ my: 0.5 }} />
          <DetailRow label="Status" value={statusConf.label} />
          <Divider sx={{ my: 0.5 }} />
          <DetailRow label="Requested" value={formatDate(request.requested_date)} />
          {request.description && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mb: 0.3 }}>Description</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.5 }}>{request.description}</Typography>
              </Box>
            </>
          )}
        </DetailSection>

        {/* Farmer Info */}
        <DetailSection title="Farmer Info" icon={<Person />} color="#059669">
          <DetailRow label="Name" value={request.user_name} />
          <Divider sx={{ my: 0.5 }} />
          <DetailRow label="Farmer ID" value={`#${request.user_id}`} mono />
        </DetailSection>

        {/* Pond Info */}
        <DetailSection title="Pond Info" icon={<Pool />} color="#0891b2">
          <DetailRow label="Pond" value={request.pond_name} />
          <Divider sx={{ my: 0.5 }} />
          <DetailRow label="Pond ID" value={`#${request.pond_id}`} mono />
        </DetailSection>

        {/* Financial Impact */}
        {(request.quantity || request.estimated_cost || request.loan_amount) && (
          <DetailSection title="Financial Impact" icon={<AttachMoney />} color="#d97706">
            {request.quantity && (
              <>
                <DetailRow label="Quantity" value={`${request.quantity} ${request.unit || ''}`} />
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {request.estimated_cost && (
              <>
                <DetailRow label="Est. Cost" value={formatCurrency(request.estimated_cost)} />
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {request.doc_period && (
              <>
                <DetailRow label="DOC Period" value={`${request.doc_period} days`} />
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {request.expected_harvest_date && (
              <>
                <DetailRow label="Exp. Harvest" value={formatDate(request.expected_harvest_date)} />
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {request.loan_amount && (
              <>
                <DetailRow label="Loan Amount" value={formatCurrency(request.loan_amount)} />
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {request.loan_type && (
              <>
                <DetailRow label="Loan Type" value={LOAN_TYPES[request.loan_type] || request.loan_type} />
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {request.repayment_period && (
              <DetailRow label="Repayment" value={`${request.repayment_period} months`} />
            )}
          </DetailSection>
        )}

        {/* Approval / Rejection Info */}
        {request.status === 'approved' && request.approver_name && (
          <Alert severity="success" sx={{ borderRadius: 2, mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Approved by {request.approver_name}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>{formatDate(request.approved_at)}</Typography>
          </Alert>
        )}

        {request.status === 'rejected' && request.rejection_reason && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Rejected</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mt: 0.3 }}>{request.rejection_reason}</Typography>
          </Alert>
        )}
      </Box>

      {/* ── Action Buttons (sticky bottom) ── */}
      {isPending && (
        <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexShrink: 0, display: 'flex', gap: 1 }}>
          <Button
            fullWidth variant="contained" size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => onApprove(request)}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1,
              bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' },
            }}
          >
            Approve
          </Button>
          <Button
            fullWidth variant="contained" size="small"
            startIcon={<CancelIcon />}
            onClick={() => onReject(request)}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1,
              bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' },
            }}
          >
            Reject
          </Button>
          <Button
            variant="outlined" size="small"
            startIcon={<ArrowUpward />}
            onClick={() => {/* Escalate handler — placeholder */}}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1, minWidth: 110, borderColor: '#d97706', color: '#d97706', '&:hover': { borderColor: '#b45309', bgcolor: '#fffbeb' } }}
          >
            Escalate
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const RequestManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Action dialog
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // ── Load Data ──
  const loadRequests = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const response = await requestsAPI.getAllAdmin();
      setRequests(response.data || []);
    } catch (err) {
      console.error('Error loading requests:', err);
      setSnackbar({ open: true, message: 'Failed to load requests', severity: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // ── Derived Data ──
  const kpis = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter(r => r.status === 'pending').length;
    const approvedToday = requests.filter(r => r.status === 'approved' && isToday(r.approved_at)).length;
    const rejectedToday = requests.filter(r => r.status === 'rejected' && isToday(r.approved_at)).length;
    const highPriority = requests.filter(r => r.status === 'pending' && derivePriority(r) === 'high').length;
    return { total, pending, approvedToday, rejectedToday, highPriority };
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      // Search
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const match = (r.request_number || '').toLowerCase().includes(q)
          || (r.title || '').toLowerCase().includes(q)
          || (r.user_name || '').toLowerCase().includes(q)
          || (r.pond_name || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      // Type
      if (typeFilter !== 'all' && r.request_type !== typeFilter) return false;
      // Status
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      // Priority
      if (priorityFilter !== 'all' && derivePriority(r) !== priorityFilter) return false;
      return true;
    });
  }, [requests, searchTerm, typeFilter, statusFilter, priorityFilter]);

  const paginatedRequests = useMemo(
    () => filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRequests, page, rowsPerPage]
  );

  // ── Actions ──
  const handleApprove = (req) => {
    setActionTarget(req);
    setActionType('approve');
    setOpenActionDialog(true);
  };

  const handleReject = (req) => {
    setActionTarget(req);
    setActionType('reject');
    setRejectionReason('');
    setOpenActionDialog(true);
  };

  const handleActionConfirm = async () => {
    try {
      if (actionType === 'approve') {
        await requestsAPI.approve(actionTarget.id);
        setSnackbar({ open: true, message: 'Request approved successfully', severity: 'success' });
      } else if (actionType === 'reject') {
        if (!rejectionReason.trim()) {
          setSnackbar({ open: true, message: 'Please provide a rejection reason', severity: 'error' });
          return;
        }
        await requestsAPI.reject(actionTarget.id, rejectionReason);
        setSnackbar({ open: true, message: 'Request rejected', severity: 'success' });
      }
      setOpenActionDialog(false);
      setActionTarget(null);
      setActionType('');
      setRejectionReason('');
      setSelectedRequest(null);
      loadRequests(true);
    } catch (err) {
      console.error('Error processing request:', err);
      setSnackbar({ open: true, message: 'Failed to process request', severity: 'error' });
    }
  };

  const handleCloseAction = () => {
    setOpenActionDialog(false);
    setActionTarget(null);
    setActionType('');
    setRejectionReason('');
  };

  // ── Loading ──
  if (loading && requests.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <CircularLoader />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading requests…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ KPI STRIP ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-3">
        <KPICard icon={<Assignment />} label="Total Requests" value={kpis.total} color="#2563eb"
          onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); }} active={statusFilter === 'all' && priorityFilter === 'all'} />
        <KPICard icon={<PendingActions />} label="Pending" value={kpis.pending} color="#d97706"
          onClick={() => { setStatusFilter('pending'); setPriorityFilter('all'); setPage(0); }} active={statusFilter === 'pending'} />
        <KPICard icon={<ThumbUp />} label="Approved Today" value={kpis.approvedToday} color="#16a34a" />
        <KPICard icon={<ThumbDown />} label="Rejected Today" value={kpis.rejectedToday} color="#dc2626" />
        <KPICard icon={<PriorityHigh />} label="High Priority" value={kpis.highPriority} color="#9333ea"
          onClick={() => { setStatusFilter('pending'); setPriorityFilter('high'); setPage(0); }} active={priorityFilter === 'high'} />
      </Box>

      {/* ══════════ FILTER BAR ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box className="flex flex-wrap items-center gap-2">
            <TextField
              size="small"
              placeholder="Search request #, title, farmer, pond…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              }}
            />
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Filters {showFilters ? <ExpandLess sx={{ ml: 0.5 }} /> : <ExpandMore sx={{ ml: 0.5 }} />}
            </Button>
            <Box className="flex-1" />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => loadRequests(true)} disabled={refreshing}>
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Chip label={`${filteredRequests.length} of ${requests.length}`} size="small"
              sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Box>

          <Collapse in={showFilters}>
            <Box className="flex flex-wrap items-center gap-2 mt-2 pt-2" sx={{ borderTop: '1px solid #e2e8f0' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Request Type</InputLabel>
                <Select value={typeFilter} label="Request Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Types</MenuItem>
                  {Object.entries(REQUEST_TYPES).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      <Box className="flex items-center gap-1.5">
                        {val.icon}
                        <span>{val.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="fulfilled">Fulfilled</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Priority</InputLabel>
                <Select value={priorityFilter} label="Priority" onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Priority</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>

              {(typeFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || searchTerm) && (
                <Button size="small"
                  onClick={() => { setTypeFilter('all'); setStatusFilter('all'); setPriorityFilter('all'); setSearchTerm(''); setPage(0); }}
                  sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600 }}>
                  Clear All
                </Button>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ══════════ SPLIT VIEW ══════════ */}
      <Box className="flex gap-2.5" sx={{ height: 'calc(100vh - 310px)', minHeight: 480 }}>
        {/* ── LEFT: Request List ── */}
        <Card sx={{ flex: selectedRequest ? '0 0 55%' : '1 1 100%', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'flex 0.3s ease' }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HEADER_SX, width: '4%' }}>#</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: '13%' }}>Req #</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: '10%' }}>Type</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: selectedRequest ? '0' : '20%', display: selectedRequest ? 'none' : 'table-cell' }}>Title</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: '14%' }}>Farmer</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: selectedRequest ? '0' : '12%', display: selectedRequest ? 'none' : 'table-cell' }}>Pond</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: '9%', textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: '8%', textAlign: 'center' }}>Priority</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: '10%' }}>Date</TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: selectedRequest ? '0' : '10%', display: selectedRequest ? 'none' : 'table-cell', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 8, textAlign: 'center' }}>
                      <Assignment sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                      <Typography sx={{ color: '#94a3b8', fontWeight: 600, mb: 0.5 }}>No Requests Found</Typography>
                      <Typography sx={{ color: '#cbd5e1', fontSize: '0.8rem' }}>
                        {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' ? 'Adjust filters to see results' : 'No request records available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRequests.map((req, idx) => {
                    const typeInfo = REQUEST_TYPES[req.request_type] || { label: req.request_type, icon: <InfoIcon />, color: '#64748b', bg: '#f1f5f9' };
                    const statusConf = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                    const priority = derivePriority(req);
                    const prioConf = PRIORITY_CONFIG[priority];
                    const isSelected = selectedRequest?.id === req.id;

                    return (
                      <TableRow
                        key={req.id}
                        hover
                        onClick={() => setSelectedRequest(req)}
                        sx={{
                          cursor: 'pointer',
                          height: 44,
                          bgcolor: isSelected ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafbfc',
                          borderLeft: `3px solid ${statusConf.border || '#e2e8f0'}`,
                          '&:hover': { bgcolor: isSelected ? '#eff6ff' : '#f0f7ff !important' },
                          transition: 'background 0.15s ease',
                        }}
                      >
                        <TableCell sx={{ py: 0.6, px: 1.5, color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600 }}>
                          {page * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell sx={{ py: 0.6, px: 1.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', fontFamily: 'monospace' }}>
                            {req.request_number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.6, px: 1.5 }}>
                          <Chip
                            icon={typeInfo.icon}
                            label={typeInfo.label}
                            size="small"
                            sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: typeInfo.bg, color: typeInfo.color, '& .MuiChip-icon': { color: typeInfo.color } }}
                          />
                        </TableCell>
                        {!selectedRequest && (
                          <TableCell sx={{ py: 0.6, px: 1.5 }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {req.title}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 0.6, px: 1.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {req.user_name}
                          </Typography>
                        </TableCell>
                        {!selectedRequest && (
                          <TableCell sx={{ py: 0.6, px: 1.5 }}>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {req.pond_name}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 0.6, textAlign: 'center' }}>
                          <Chip label={statusConf.label} size="small"
                            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: statusConf.bg, color: statusConf.color }} />
                        </TableCell>
                        <TableCell sx={{ py: 0.6, textAlign: 'center' }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: prioConf.color, mx: 'auto' }} title={prioConf.label} />
                        </TableCell>
                        <TableCell sx={{ py: 0.6, px: 1.5 }}>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {formatDate(req.requested_date)}
                          </Typography>
                        </TableCell>
                        {!selectedRequest && (
                          <TableCell sx={{ py: 0.6, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <Box className="flex items-center justify-center gap-0.5">
                              <Tooltip title="View">
                                <IconButton size="small" onClick={() => setSelectedRequest(req)} sx={{ color: '#2563eb' }}>
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {req.status === 'pending' && (
                                <>
                                  <Tooltip title="Approve">
                                    <IconButton size="small" onClick={() => handleApprove(req)} sx={{ color: '#16a34a' }}>
                                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton size="small" onClick={() => handleReject(req)} sx={{ color: '#dc2626' }}>
                                      <CancelIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ borderTop: '1px solid #e2e8f0', flexShrink: 0 }}
          />
        </Card>

        {/* ── RIGHT: Detail Panel ── */}
        {selectedRequest && (
          <Card sx={{
            flex: '0 0 44%',
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideIn 0.2s ease',
            '@keyframes slideIn': { from: { opacity: 0, transform: 'translateX(16px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
          }}>
            <RequestDetailPanel
              request={selectedRequest}
              onApprove={handleApprove}
              onReject={handleReject}
              onClose={() => setSelectedRequest(null)}
            />
          </Card>
        )}
      </Box>

      {/* ══════════ ACTION CONFIRMATION DIALOG ══════════ */}
      <Dialog open={openActionDialog} onClose={handleCloseAction} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: actionType === 'approve' ? '#f0fdf4' : '#fef2f2', fontWeight: 700, fontSize: '1rem' }}>
          {actionType === 'approve' ? '✓ Approve Request' : '✕ Reject Request'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {actionTarget && (
            <Box>
              <Box className="grid grid-cols-2 gap-2 mb-3">
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Request</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>{actionTarget.request_number}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Type</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                    {(REQUEST_TYPES[actionTarget.request_type] || {}).label || actionTarget.request_type}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Farmer</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{actionTarget.user_name}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase' }}>Amount</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    {formatCurrency(actionTarget.estimated_cost || actionTarget.loan_amount)}
                  </Typography>
                </Box>
              </Box>

              {actionType === 'reject' && (
                <TextField
                  autoFocus fullWidth multiline rows={3} size="small"
                  label="Rejection Reason *"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason…"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}

              {actionType === 'approve' && (
                <Alert severity="info" sx={{ borderRadius: 2, mt: 1 }}>
                  This will approve the request and notify the farmer. This action cannot be undone.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAction} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            disabled={actionType === 'reject' && !rejectionReason.trim()}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              bgcolor: actionType === 'approve' ? '#16a34a' : '#dc2626',
              '&:hover': { bgcolor: actionType === 'approve' ? '#15803d' : '#b91c1c' },
            }}
          >
            {actionType === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/** Simple circular loader */
const CircularLoader = () => (
  <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
);

export default RequestManagement;
